import { formatDate, formatMoney, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { type ReactElement, useState } from "react";

import type { DoorResult } from "../session/useSession.js";
import type { DealerSettlement } from "../session/settlements.js";

export { type DealerSettlement, dealerSettlementsFrom } from "../session/settlements.js";

/**
 * `round T-2`, `M5` (`P2-DLW-011`, `P2-DLW-012`, `P2-DLW-017`; WF-DLR-11; `DECISION ADDED` 20):
 * **the settlement card with two rows, one per leg**, the dealer's OWN declaration, the handover
 * against the buyer's bon de retrait.
 *
 * `GET /api/v1/me/settlements` is the card's one read: the settlement standing on this car as
 * `payments.v_settlements` derives it. « Votre part » (the dealer's 90 %, on his own account,
 * recorded by him) and « Commission Hakili » (Hakili's 10 %, on Hakili's account, observed by
 * Hakili) , the payer named, no net line and no deduction. « Je confirme avoir reçu mes 90 % »
 * with the date of receipt calls `confirm-receipt` once with an `Idempotency-Key`; « Je n'ai rien
 * reçu » calls `report-non-receipt` and leaves the bon blocked. The strip reads « Règlement
 * partiel » in amber while exactly one leg stands (WF-DLR-11 step 4). The handover control is
 * disabled with its reason while the bon is not issued (I19, I34), live with the reference typed;
 * no word of delivery anywhere (I12).
 */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** WF-DLR-11's two disabled reasons on the handover, the third for both legs awaited. */
export function handoverReason(settlement: DealerSettlement): string | null {
  if (settlement.voucher !== null) return null;
  if (settlement.missing === "concessionnaire") return t("dealer.reglement.remiseMotifVotrePart");
  if (settlement.missing === "hakili") return t("dealer.reglement.remiseMotifHakili");
  return t("dealer.reglement.remiseMotifDeux");
}

export function SettlementCard(props: {
  readonly settlement: DealerSettlement;
  readonly confirmReceipt: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly handover: (vehicleId: string, voucherReference: string) => Promise<DoorResult<unknown>>;
  readonly onChanged: () => void;
}): ReactElement {
  const { settlement } = props;
  const [receivedOn, setReceivedOn] = useState("");
  const [presented, setPresented] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const dealerConfirmed = settlement.dealerLegConfirmedAt !== null;
  const hakiliReceived = settlement.hakiliLegConfirmedAt !== null;
  const partial = dealerConfirmed !== hakiliReceived;
  const complete = dealerConfirmed && hakiliReceived;
  const handedOver = settlement.voucher?.presentedAt ?? null;
  const reason = handoverReason(settlement);
  const act = async (door: () => Promise<DoorResult<unknown>>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const answer = await door();
    setBusy(false);
    if (!answer.ok) {
      setRefusal(answer.message);
      return;
    }
    props.onChanged();
  };
  return (
    <div data-testid="vehicule-reglement" className="mt-4 rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">{t("dealer.reglement.titre")}</h3>
        <span className="text-xs text-gray-500" data-testid="vehicule-reglement-payeur">
          {settlement.payerKind === "acheteur"
            ? t("dealer.reglement.payeurAcheteur")
            : t("dealer.reglement.payeurBanque")}
        </span>
      </div>
      {/* Two rows, one per leg , no net, no deduction: the dealer never receives the other 10 %. */}
      <table className="mt-3 w-full text-sm" data-testid="vehicule-reglement-volets">
        <tbody>
          <tr data-testid="vehicule-reglement-votre-part">
            <td className="py-1.5">
              <span className="block font-medium text-gray-900">{t("dealer.reglement.votrePart")}</span>
              <span className="block text-xs text-gray-500">
                {fill("dealer.reglement.votrePartMots", { taux: String(100 - settlement.ratePct) })}
              </span>
            </td>
            <td className="py-1.5 text-right font-semibold tabular-nums text-gray-900">
              {formatMoney(settlement.dealerAmount)}
            </td>
            <td className="py-1.5 pl-3 text-right text-xs" data-testid="vehicule-reglement-votre-part-etat">
              {settlement.dealerLegConfirmedAt === null
                ? t("dealer.reglement.enAttenteVous")
                : fill("dealer.reglement.confirmeeLe", {
                    date: formatDate(new Date(settlement.dealerLegConfirmedAt)),
                  })}
            </td>
          </tr>
          <tr data-testid="vehicule-reglement-commission">
            <td className="py-1.5">
              <span className="block font-medium text-gray-900">
                {t("dealer.reglement.commissionHakili")}
              </span>
              <span className="block text-xs text-gray-500">
                {fill("dealer.reglement.commissionHakiliMots", { taux: String(settlement.ratePct) })}
              </span>
            </td>
            <td className="py-1.5 text-right font-semibold tabular-nums text-gray-900">
              {formatMoney(settlement.hakiliAmount)}
            </td>
            <td className="py-1.5 pl-3 text-right text-xs" data-testid="vehicule-reglement-commission-etat">
              {settlement.hakiliLegConfirmedAt === null
                ? t("dealer.reglement.enAttente")
                : fill("dealer.reglement.recueLe", {
                    date: formatDate(new Date(settlement.hakiliLegConfirmedAt)),
                  })}
            </td>
          </tr>
        </tbody>
      </table>

      {/* WF-DLR-11 step 4: the strip, always visible , « Règlement partiel » in amber while exactly one leg stands. */}
      <div
        data-testid="vehicule-reglement-bandeau"
        className={
          "mt-3 rounded-xl px-3 py-2 text-xs " +
          (complete
            ? "bg-emerald-50 text-emerald-800"
            : partial
              ? "bg-amber-50 text-amber-700"
              : "bg-gray-50 text-gray-600")
        }
      >
        {complete ? (
          <>
            <span className="font-medium">{t("dealer.reglement.complet")}</span>
            {" · "}
            {fill("dealer.reglement.completMots", {
              reference: settlement.voucher?.reference ?? t("common.valeur.tiret"),
            })}
          </>
        ) : partial ? (
          <>
            <span className="font-medium">{t("dealer.reglement.partiel")}</span>
            {" · "}
            {t("dealer.reglement.partielMots")}
          </>
        ) : (
          t("dealer.reglement.enAttente")
        )}
      </div>

      {dealerConfirmed ? null : (
        <div
          className="mt-3 rounded-xl border border-gray-200 p-3"
          data-testid="vehicule-reglement-confirmation"
        >
          <label className="block text-xs text-gray-600">
            {t("dealer.reglement.dateReception")}
            <input
              type="date"
              value={receivedOn}
              onChange={(event) => {
                setReceivedOn(event.target.value);
              }}
              data-testid="vehicule-reglement-date"
              className="mt-1 block rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {receivedOn === "" ? (
              <GuardedButton
                disabled
                reason={t("dealer.reglement.confirmerMotif")}
                variant="primary"
                testId="vehicule-reglement-confirmer"
              >
                {fill("dealer.reglement.confirmer", {
                  taux: String(100 - settlement.ratePct),
                  montant: formatMoney(settlement.dealerAmount),
                })}
              </GuardedButton>
            ) : (
              <button
                type="button"
                disabled={busy}
                data-testid="vehicule-reglement-confirmer"
                onClick={() => {
                  void act(() => props.confirmReceipt(settlement.id, receivedOn));
                }}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
              >
                {fill("dealer.reglement.confirmer", {
                  taux: String(100 - settlement.ratePct),
                  montant: formatMoney(settlement.dealerAmount),
                })}
              </button>
            )}
            <button
              type="button"
              disabled={busy || settlement.dealerNonReceiptReportedAt !== null}
              data-testid="vehicule-reglement-rien-recu"
              onClick={() => {
                void act(() => props.reportNonReceipt(settlement.id));
              }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-60"
            >
              {t("dealer.reglement.rienRecu")}
            </button>
          </div>
          {settlement.dealerNonReceiptReportedAt === null ? null : (
            <p className="mt-2 text-xs text-amber-700" data-testid="vehicule-reglement-rien-recu-declare">
              {fill("dealer.reglement.rienRecuDeclare", {
                date: formatDate(new Date(settlement.dealerNonReceiptReportedAt)),
              })}
            </p>
          )}
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500" data-testid="vehicule-reglement-copie">
        {fill("dealer.reglement.copie", { acheteur: settlement.clientName ?? t("common.valeur.tiret") })}
      </p>

      {/* WF-DLR-11 step 5: the handover against the bon, on the lot. */}
      {handedOver !== null ? (
        <p className="mt-3 text-sm font-medium text-emerald-700" data-testid="vehicule-reglement-remis">
          {fill("dealer.reglement.remisLe", {
            date: formatDate(new Date(handedOver)),
            reference: settlement.voucher?.reference ?? t("common.valeur.tiret"),
          })}
        </p>
      ) : reason !== null ? (
        <div className="mt-3">
          <GuardedButton disabled reason={reason} variant="secondary" testId="vehicule-reglement-remise">
            {t("dealer.reglement.remise")}
          </GuardedButton>
        </div>
      ) : (
        <div
          className="mt-3 flex flex-wrap items-end gap-2"
          data-testid="vehicule-reglement-remise-formulaire"
        >
          <label className="block text-xs text-gray-600">
            {t("dealer.reglement.remiseReference")}
            <input
              type="text"
              value={presented}
              onChange={(event) => {
                setPresented(event.target.value);
              }}
              data-testid="vehicule-reglement-remise-reference"
              className="mt-1 block rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
          {presented.trim() === "" ? (
            <GuardedButton
              disabled
              reason={t("dealer.reglement.remiseMotifReference")}
              variant="secondary"
              testId="vehicule-reglement-remise"
            >
              {t("dealer.reglement.remise")}
            </GuardedButton>
          ) : (
            <button
              type="button"
              disabled={busy}
              data-testid="vehicule-reglement-remise"
              onClick={() => {
                void act(() => props.handover(settlement.vehicleId, presented.trim()));
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {t("dealer.reglement.remise")}
            </button>
          )}
        </div>
      )}
      {refusal === null ? null : (
        <p role="alert" data-testid="vehicule-reglement-refus" className="mt-2 text-xs text-rose-600">
          {refusal}
        </p>
      )}
    </div>
  );
}
